import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { SeeMoreList } from '@lumapps/lumx-lists/components/SeeMoreList';
import { Chip, Size, FlexBox, Text, ChipProps, FlexBoxProps } from '@lumapps/lumx/react';

import { DrawerSection, DrawerSectionProps } from '../DrawerSection';

import './index.scss';

export interface DrawerChipsSectionProps<T> extends DrawerSectionProps {
    items?: T[];
    getItemLabel: (item: T) => string;
    getItemId: (item: T) => string;
    chipProps?: Partial<ChipProps>;
    chipWrapperProps?: Partial<FlexBoxProps>;
}

const CLASSNAME = 'lumx-drawer-chips-section';
/**
 * Displays a list of chips inside the context of a Drawer
 * @family Drawer
 * @param DrawerChipsSectionProps
 * @returns DrawerChipsSection
 */
export const DrawerChipsSection = <T,>({
    items,
    getItemLabel,
    getItemId,
    chipProps,
    chipWrapperProps,
    ...props
}: DrawerChipsSectionProps<T>) => {
    const { block, element } = useClassnames(CLASSNAME);

    const chipRenderer = React.useCallback(
        (item: T) => (
            <Chip size={Size.s} key={getItemId(item)}>
                <Text as="span" truncate {...chipProps} className={element('chip', [chipProps?.className])}>
                    {getItemLabel(item)}
                </Text>
            </Chip>
        ),
        [chipProps, element, getItemId, getItemLabel],
    );

    return (
        <DrawerSection
            {...props}
            wrapperProps={{
                ...props.wrapperProps,
                className: block([props.wrapperProps?.className]),
            }}
        >
            {items ? (
                <FlexBox
                    orientation="horizontal"
                    wrap
                    gap="regular"
                    {...chipWrapperProps}
                    className={element('chips', [chipWrapperProps?.className])}
                >
                    <SeeMoreList items={items} maxItems={10} itemRenderer={chipRenderer} />
                </FlexBox>
            ) : null}
        </DrawerSection>
    );
};
